import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useCreateClaimPerson, useGetPolicyPersonEnums } from '@common/slices';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import type { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import type {
  ClaimPersonDrawerProps,
  ClaimPersonFormData,
  CreateClaimPersonVariables
} from '@common/types';
import type { IAbstractRecord } from '@models';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import {
  getInputs,
  initialValues
} from '../content/claims-person-drawer/content';
import { LookupToList } from '../../features/claims-management/claims/drawers/new-person-drawer/utils';

import { cloneDeep, isEmpty } from 'lodash';
import DateService from '@core/services/DateService';

const ClaimPersonDrawer: React.FC<ClaimPersonDrawerProps> = ({
  claimId,
  open,
  onClose,
  onSubmitSuccess,
  section
}) => {
  const [booted, setBooted] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>([]);

  const logPrefix = `[CLAIM PERSON DRAWER:${section}]`;

  const { data: enumsData } = useGetPolicyPersonEnums();
  const [createPerson] = useCreateClaimPerson();
  const initialize = async () => {
    try {
      if (!enumsData) return;

      const lovData = LookupToList(enumsData);

      setLovs(lovData);
      setInputsForm(getInputs(initialValues, lovData, section));
      setBooted(true);
    } catch (e) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (formValues: IAbstractRecord) => {
    const [data] = normalizeDynamicFormDrawerValues(
      inputsForm,
      formValues
    ) as [ClaimPersonFormData];

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variables: CreateClaimPersonVariables = {
        claimID: claimId,
        personInput: {
          type: data.type,
          title: data.title,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          dateOfBirth: data.dateOfBirth
            ? DateService.formatDateBackend(data.dateOfBirth)
            : null,
          gender: data.gender,
          nationality: data.nationality?.replace('_', '') || null,
          companyName: data.companyName,
          email: data.email,
          phoneType: data.phoneType,
          phoneNumber: data.phoneNumber,
          passportNumber: data.passportNumber,
          address: {
            country: data.country,
            zone: data.zone,
            city: data.city,
            street: data.street,
            building: data.building,
            addressDetails: data.addressDetails,
            addressDetailsArabic: data.addressDetailsArabic
          }
        }
      };

      const res = await createPerson(variables);

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>Person added successfully</ToastSuccessMessage>
        );

        const newId = res.data?.claimManagement?.createClaimPerson;

        onSubmitSuccess?.({
          id: newId,
          ...data
        });

        setSubmitButtonState('success');
        setFormDisabled(false);
        onClose();
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (e) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      console.error(logPrefix, e);

      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const onChange = async (
    fieldName: string,
    value: unknown,
    sectionIndex: number,
    newValues: IAbstractRecord
  ) => {
    const newLovs = cloneDeep(lovs);

    if (fieldName === 'country' && value !== 'LB') {
      newValues.zone = null;
    }

    setInputsForm(getInputs(newValues, newLovs, section));
  };

  useEffect(() => {
    if (enumsData) {
      initialize();
    }
  }, [enumsData]);
  if (!booted) return null;

  return (
    <DynamicFormDrawer
      title="Add Person"
      isOpen={open}
      onClose={onClose}
      sections={inputsForm}
      onSubmit={submitForm}
      onChange={onChange}
      disableForm={formDisabled}
      submitButtonState={submitButtonState}
    />
  );
};

export default ClaimPersonDrawer;
